<script setup lang="ts">
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  CircleX,
  Download,
  HelpCircle,
  Loader2,
  Play,
  RefreshCw,
  ScanSearch,
  Server,
  Upload,
  UserPlus,
  Users
} from 'lucide-vue-next'
import type { FunctionalComponent } from 'vue'
import type { LucideProps } from 'lucide-vue-next'

import type { CustomerImportDiagnostics, ImportSummary } from '@/types/types'
import { translateImportRejectionMessage } from '@/utils/importRejectionMessage'

const props = withDefaults(
  defineProps<{
    importId: number | null
    diagnostics: CustomerImportDiagnostics | null
    isLoading: boolean
    isSupportConnection?: boolean
    showLaunchActions?: boolean
    isLaunching?: boolean
  }>(),
  {
    isSupportConnection: false,
    showLaunchActions: false,
    isLaunching: false
  }
)

const emit = defineEmits<{
  download: [importId: number, fileName: string]
  exportErrors: [importId: number, fileName: string]
  launch: []
}>()

const { t, te } = useI18n()

const isProcessingStarted = computed(() => {
  if (!props.diagnostics) {
    return false
  }

  return (
    props.diagnostics.isComplete || props.diagnostics.stage === 'processing' || Boolean(props.diagnostics.startedAt)
  )
})

const percentageOf = (done: number, total: number): number => {
  return total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
}

const isPreparationComplete = computed(() => {
  if (!props.diagnostics) {
    return false
  }

  const { expectedLines, parsedLines, isComplete } = props.diagnostics

  return (expectedLines > 0 && parsedLines >= expectedLines) || isComplete
})

const preparationSummary = computed((): ImportSummary | null => {
  if (!props.diagnostics?.summary) {
    return null
  }

  return {
    ...props.diagnostics.summary,
    noPhoneNewCustomersCount: props.diagnostics.summary.noPhoneNewCustomersCount ?? 0
  }
})

const hasPreparationSummaryContent = computed((): boolean => {
  if (!props.diagnostics) {
    return false
  }

  return props.diagnostics.parsedLines > 0 || props.diagnostics.totalCustomersWithError > 0
})

const preparationLinesProgress = computed(() => {
  if (!props.diagnostics || props.diagnostics.expectedLines <= 0) {
    return null
  }

  return {
    processed: props.diagnostics.parsedLines,
    total: props.diagnostics.expectedLines
  }
})

const preparationLinesPercentage = computed(() => {
  if (!preparationLinesProgress.value) {
    return 0
  }

  return percentageOf(preparationLinesProgress.value.processed, preparationLinesProgress.value.total)
})

const processingPercentage = computed(() => {
  if (!props.diagnostics) {
    return 0
  }

  return percentageOf(props.diagnostics.totalCustomersProcessed, props.diagnostics.totalCustomers)
})

const formatDuration = (minutes: number): string => {
  if (minutes >= 1440) {
    return t('pages.import.historyPage.duration.days', { count: Math.floor(minutes / 1440) })
  }

  if (minutes >= 60) {
    return t('pages.import.historyPage.duration.hours', { count: Math.floor(minutes / 60) })
  }

  return t('pages.import.historyPage.duration.minutes', { count: minutes })
}

const formatTimelineDate = (value: string | null): { date: string; time: string } | null => {
  if (!value) {
    return null
  }

  const date = new Date(value.replace(' ', 'T'))

  return {
    date: date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
    time: date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }
}

type TimelineStatus = 'complete' | 'current' | 'upcoming' | 'blocked'

type TimelineStep = {
  key: string
  label: string
  hint: string | null
  formattedDate: { date: string; time: string } | null
  status: TimelineStatus
  icon: FunctionalComponent<LucideProps>
}

const blockedStepKey = computed((): string | null => {
  if (!props.diagnostics?.isStalled || !props.diagnostics.blockedReason) {
    return null
  }

  const { code } = props.diagnostics.blockedReason

  if (code === 'pending_no_lines' || code === 'parsing_stopped') {
    return code === 'pending_no_lines' ? 'starting_preparation' : 'parsing'
  }

  if (code === 'parsing_complete_not_started' || code === 'processing_stopped') {
    return 'processing'
  }

  return null
})

const blockedStepHint = computed((): string | null => {
  if (!props.diagnostics?.isStalled) {
    return null
  }

  const parts: string[] = [t('pages.import.historyPage.diagnosticsModal.timelineBlocked')]

  if (props.diagnostics.stalledForMinutes) {
    parts.push(
      t('pages.import.historyPage.diagnosticsModal.timelineBlockedSince', {
        duration: formatDuration(props.diagnostics.stalledForMinutes)
      })
    )
  }

  return parts.join(' · ')
})

const applyBlockedStatus = (stepKey: string, status: TimelineStatus): TimelineStatus => {
  if (blockedStepKey.value === stepKey) {
    return 'blocked'
  }

  return status
}

const isWaitingForLaunchConfirmation = computed(() => {
  return isPreparationComplete.value && !isProcessingStarted.value
})

const showPreparationSummaryPreview = computed((): boolean => {
  if (!props.diagnostics || props.diagnostics.isComplete) {
    return false
  }

  return !isProcessingStarted.value && !isPreparationComplete.value
})

const showPreparationSummaryBeforeLaunch = computed((): boolean => {
  if (!hasPreparationSummaryContent.value || !props.diagnostics) {
    return false
  }

  return isWaitingForLaunchConfirmation.value || props.diagnostics.isComplete
})

const showPendingContactLabels = computed(() => !isProcessingStarted.value)

type PreparationSummaryCard = {
  key: string
  label: string
  value: number
  icon: FunctionalComponent<LucideProps>
  containerClass: string
  iconClass: string
  valueClass: string
  labelClass: string
}

const preparationSummaryCards = computed((): PreparationSummaryCard[] => {
  if (!preparationSummary.value) {
    return []
  }

  const summary = preparationSummary.value
  const pending = showPendingContactLabels.value

  return [
    {
      key: 'new',
      label: pending
        ? t('pages.import.importPage.finalize.newContactsPending')
        : t('pages.import.importPage.finalize.newContacts'),
      value: summary.newCustomersCount,
      icon: UserPlus,
      containerClass: 'border-emerald-200 bg-emerald-50/70',
      iconClass: 'bg-emerald-100 text-emerald-600',
      valueClass: 'text-emerald-700',
      labelClass: 'text-emerald-900/70'
    },
    {
      key: 'updated',
      label: pending
        ? t('pages.import.importPage.finalize.updatedContactsPending')
        : t('pages.import.importPage.finalize.updatedContacts'),
      value: summary.existedCustomersCount,
      icon: RefreshCw,
      containerClass: 'border-amber-200 bg-amber-50/70',
      iconClass: 'bg-amber-100 text-amber-600',
      valueClass: 'text-amber-700',
      labelClass: 'text-amber-900/70'
    },
    {
      key: 'ignored',
      label: pending
        ? t('pages.import.importPage.finalize.ignoredContactsPending')
        : t('pages.import.importPage.finalize.ignoredContacts'),
      value: summary.ignoredLinesCount,
      icon: CircleX,
      containerClass: 'border-red-200 bg-red-50/70',
      iconClass: 'bg-red-100 text-red-600',
      valueClass: 'text-red-700',
      labelClass: 'text-red-900/70'
    }
  ]
})

const timelineSteps = computed((): TimelineStep[] => {
  if (!props.diagnostics) {
    return []
  }

  const { diagnostics } = props
  const parsingDone = diagnostics.expectedLines > 0 && diagnostics.parsedLines >= diagnostics.expectedLines
  const isWaitingForPreparationStart = !diagnostics.isComplete && !parsingDone && diagnostics.parsedLines === 0

  const startingPreparationStatus = applyBlockedStatus(
    'starting_preparation',
    parsingDone || diagnostics.isComplete || diagnostics.parsedLines > 0
      ? 'complete'
      : isWaitingForPreparationStart
        ? 'current'
        : 'upcoming'
  )

  const parsingStatus = applyBlockedStatus(
    'parsing',
    isProcessingStarted.value || diagnostics.isComplete || isPreparationComplete.value
      ? 'complete'
      : diagnostics.parsedLines > 0 || parsingDone
        ? 'current'
        : 'upcoming'
  )

  const processingStatus = applyBlockedStatus(
    'processing',
    diagnostics.isComplete
      ? 'complete'
      : diagnostics.stage === 'processing'
        ? 'current'
        : isWaitingForLaunchConfirmation.value
          ? 'current'
          : diagnostics.startedAt
            ? 'complete'
            : 'upcoming'
  )

  const parsingHint =
    parsingStatus === 'blocked'
      ? blockedStepHint.value
      : parsingStatus === 'upcoming'
        ? t('pages.import.historyPage.diagnosticsModal.timelinePending')
        : null

  const startingPreparationHint =
    startingPreparationStatus === 'blocked'
      ? blockedStepHint.value
      : startingPreparationStatus === 'current'
        ? t('pages.import.historyPage.diagnosticsModal.preparationStartingHint')
        : null

  const processingHint =
    processingStatus === 'blocked'
      ? blockedStepHint.value
      : isWaitingForLaunchConfirmation.value
        ? null
        : diagnostics.startedAt
          ? null
          : t('pages.import.historyPage.diagnosticsModal.notStarted')

  return [
    ...(diagnostics.isComplete
      ? [
          {
            key: 'uploaded',
            label: t('pages.import.historyPage.diagnosticsModal.uploadedAt'),
            hint: null,
            formattedDate: formatTimelineDate(diagnostics.uploadedAt),
            status: 'complete' as TimelineStatus,
            icon: Upload
          }
        ]
      : []),
    ...(!diagnostics.isComplete
      ? [
          {
            key: 'starting_preparation',
            label: t('pages.import.historyPage.diagnosticsModal.preparationStarting'),
            hint: startingPreparationHint,
            formattedDate: null,
            status: startingPreparationStatus,
            icon: Server
          }
        ]
      : []),
    {
      key: 'parsing',
      label: t('pages.import.historyPage.stages.parsing'),
      hint: parsingHint,
      formattedDate:
        diagnostics.isComplete && parsingStatus !== 'blocked' ? formatTimelineDate(diagnostics.lastActivityAt) : null,
      status: parsingStatus,
      icon: ScanSearch
    },
    {
      key: 'processing',
      label: t('pages.import.historyPage.stages.processing'),
      hint: processingHint,
      formattedDate:
        diagnostics.isComplete && processingStatus !== 'blocked' ? formatTimelineDate(diagnostics.startedAt) : null,
      status: processingStatus,
      icon: Users
    },
    ...(diagnostics.isComplete
      ? [
          {
            key: 'finished',
            label: t('pages.import.historyPage.diagnosticsModal.finishedAt'),
            hint: null,
            formattedDate: formatTimelineDate(diagnostics.finishedAt),
            status: 'complete' as TimelineStatus,
            icon: CheckCircle2
          }
        ]
      : [])
  ]
})

const timelineStepClasses = (status: TimelineStatus): string => {
  if (status === 'blocked') {
    return 'border-red-500 bg-red-50 text-red-600 ring-4 ring-red-100'
  }

  if (status === 'complete') {
    return 'border-emerald-500 bg-emerald-50 text-emerald-600'
  }

  if (status === 'current') {
    return 'border-primary bg-white text-primary ring-4 ring-primary/10'
  }

  return 'border-border bg-white text-muted-foreground'
}

const timelineConnectorClasses = (status: TimelineStatus): string => {
  return status === 'complete' ? 'bg-emerald-300' : 'bg-stone-200'
}

const timelineLabelClasses = (status: TimelineStatus): string => {
  if (status === 'blocked') {
    return 'text-red-700'
  }

  if (status === 'current') {
    return 'text-primary'
  }

  if (status === 'upcoming') {
    return 'text-muted-foreground'
  }

  return 'text-foreground'
}

const diagnosticsErrorLines = (lines: number[]): string => lines.join(', ')

const rejectionReasonLabel = (message: string): string => translateImportRejectionMessage(message, t, te)

const MAX_VISIBLE_ERRORS = 8

type GroupedError = {
  label: string
  count: number
  sampleLines: number[]
}

const groupedErrors = computed((): GroupedError[] => {
  if (!props.diagnostics) {
    return []
  }

  const groups = new Map<string, GroupedError>()

  for (const error of props.diagnostics.errors) {
    const label = rejectionReasonLabel(error.message)
    const existing = groups.get(label)

    if (existing) {
      existing.count += error.count
      existing.sampleLines.push(...error.sampleLines)
    } else {
      groups.set(label, { label, count: error.count, sampleLines: [...error.sampleLines] })
    }
  }

  return Array.from(groups.values()).sort((a, b) => b.count - a.count)
})

const visibleErrors = computed(() => groupedErrors.value.slice(0, MAX_VISIBLE_ERRORS))

const hiddenErrorsCount = computed(() => Math.max(0, groupedErrors.value.length - MAX_VISIBLE_ERRORS))

const isIgnoredContactsDetailExpanded = ref(false)

const uploadedFileExtension = computed((): string => {
  const name = props.diagnostics?.name

  if (!name?.includes('.')) {
    return 'FILE'
  }

  return name.split('.').pop()?.slice(0, 4).toUpperCase() ?? 'FILE'
})

const uploadedFileSizeLabel = computed((): string | null => {
  const bytes = props.diagnostics?.file.sizeBytes

  if (bytes == null || bytes <= 0) {
    return null
  }

  if (bytes < 1024) {
    return `${bytes} o`
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} Ko`
  }

  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} Mo`
})

const sampleLinesLabel = (lines: number[]): string => {
  return diagnosticsErrorLines([...lines].sort((a, b) => a - b).slice(0, 5))
}

const blockedReasonMessage = computed((): string | null => {
  const reason = props.diagnostics?.blockedReason

  if (!reason) {
    return null
  }

  return t(`pages.import.historyPage.diagnosticsModal.blockedReasons.${reason.code}`, {
    parsed: reason.parsed,
    total: reason.total,
    processed: reason.processed
  })
})
</script>

<template>
  <div v-if="isLoading" class="flex justify-center py-16">
    <Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
  </div>

  <div v-else-if="diagnostics" class="flex flex-col gap-6">
    <Alert v-if="blockedReasonMessage" variant="destructive">
      <AlertTriangle />
      <AlertTitle>{{ t('pages.import.historyPage.diagnosticsModal.blockedReason') }}</AlertTitle>
      <AlertDescription class="flex flex-col gap-1">
        <p>{{ blockedReasonMessage }}</p>
        <p v-if="diagnostics.stalledForMinutes">
          {{
            t('pages.import.historyPage.statusTooltip.stalledSince', {
              duration: formatDuration(diagnostics.stalledForMinutes)
            })
          }}
        </p>
      </AlertDescription>
    </Alert>

    <div class="rounded-xl border border-border bg-white p-5">
      <h4 class="text-sm font-semibold text-foreground">
        {{ t('pages.import.historyPage.diagnosticsModal.progress') }}
      </h4>

      <ol class="relative mt-5 space-y-0">
        <li v-for="(step, index) in timelineSteps" :key="step.key" class="relative flex gap-4">
          <div class="relative w-8 shrink-0 self-stretch">
            <div
              v-if="index < timelineSteps.length - 1"
              class="absolute top-[calc(2rem+1px)] bottom-0 left-1/2 w-px -translate-x-1/2"
              :class="timelineConnectorClasses(step.status)"
            />
            <div
              class="relative z-10 mx-auto flex h-8 w-8 items-center justify-center rounded-full border-2"
              :class="timelineStepClasses(step.status)"
            >
              <component :is="step.status === 'blocked' ? AlertTriangle : step.icon" class="h-4 w-4" />
            </div>
          </div>
          <div class="min-w-0 flex-1 pt-0.5" :class="index < timelineSteps.length - 1 ? 'pb-8' : ''">
            <div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
              <div class="flex min-w-0 items-center gap-1.5">
                <p class="text-sm font-medium" :class="timelineLabelClasses(step.status)">
                  {{ step.label }}
                </p>
                <AppTooltip v-if="step.key === 'parsing'" tooltip-position="top-start" width="max-w-xs">
                  <button
                    type="button"
                    class="inline-flex shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                    :aria-label="t('pages.import.importPage.finalize.preparationExplanationTitle')"
                  >
                    <HelpCircle class="h-4 w-4" />
                  </button>
                  <template #tooltip-content>
                    <p class="text-left text-sm leading-relaxed">
                      {{ t('pages.import.importPage.finalize.preparationExplanation') }}
                    </p>
                  </template>
                </AppTooltip>
              </div>
              <div class="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2 pt-0.5">
                <BaseChip
                  v-if="step.status === 'blocked'"
                  color="red"
                  :text="t('pages.import.historyPage.diagnosticsModal.timelineBlocked')"
                />
                <p
                  v-if="diagnostics.isComplete && step.formattedDate"
                  class="text-right text-xs tabular-nums text-muted-foreground/60"
                >
                  {{ step.formattedDate.date }}
                  <span class="text-muted-foreground/40">· {{ step.formattedDate.time }}</span>
                </p>
              </div>
            </div>
            <p
              v-if="step.hint"
              class="mt-0.5 text-sm"
              :class="
                step.status === 'blocked'
                  ? 'text-red-600'
                  : step.status === 'current'
                    ? 'text-primary'
                    : 'text-muted-foreground'
              "
            >
              {{ step.hint }}
            </p>
            <p
              v-else-if="
                step.key !== 'parsing' &&
                step.key !== 'processing' &&
                step.key !== 'starting_preparation' &&
                step.key !== 'uploaded' &&
                step.key !== 'finished'
              "
              class="mt-0.5 text-sm text-muted-foreground"
            >
              —
            </p>

            <div v-if="step.key === 'uploaded'" class="mt-3 rounded-lg border border-border bg-muted/25 p-3">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex min-w-0 items-center gap-3">
                  <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-white text-[10px] font-bold uppercase tracking-wide text-muted-foreground"
                    :title="uploadedFileExtension"
                  >
                    {{ uploadedFileExtension }}
                  </div>
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-foreground" :title="diagnostics.name">
                      {{ diagnostics.name }}
                    </p>
                    <p v-if="uploadedFileSizeLabel || isSupportConnection" class="mt-0.5 text-xs text-muted-foreground">
                      <span v-if="uploadedFileSizeLabel">{{ uploadedFileSizeLabel }}</span>
                      <span v-if="uploadedFileSizeLabel && isSupportConnection"> · </span>
                      <span v-if="isSupportConnection">#{{ importId }}</span>
                    </p>
                  </div>
                </div>
                <Button
                  v-if="diagnostics.file.exists"
                  variant="outline"
                  size="sm"
                  type="button"
                  class="w-full shrink-0 sm:w-auto"
                  @click="emit('download', diagnostics.id, diagnostics.name)"
                >
                  <Download class="h-4 w-4" />
                  {{ t('pages.import.historyPage.diagnosticsModal.downloadFile') }}
                </Button>
              </div>
            </div>

            <div v-if="step.key === 'parsing' && step.status !== 'upcoming'" class="mt-4 flex flex-col gap-4">
              <div v-if="preparationLinesProgress" class="flex flex-col gap-2">
                <div class="flex items-center justify-between gap-2 text-sm">
                  <span class="font-medium text-foreground">
                    {{ t('pages.import.historyPage.diagnosticsModal.parsedLines') }}
                  </span>
                  <span class="font-semibold text-primary">{{ preparationLinesPercentage }}%</span>
                </div>
                <Progress :model-value="preparationLinesPercentage" class="h-2.5" />
                <p class="text-xs text-muted-foreground">
                  <template v-if="!isPreparationComplete">
                    {{
                      t('pages.import.historyPage.diagnosticsModal.preparationProgressHint', {
                        processed: preparationLinesProgress.processed,
                        total: preparationLinesProgress.total
                      })
                    }}
                  </template>
                  <template v-else>
                    {{ preparationLinesProgress.processed }} / {{ preparationLinesProgress.total }}
                    {{ t('pages.import.historyPage.diagnosticsModal.parsedLines').toLowerCase() }}
                  </template>
                </p>
              </div>

              <div
                v-if="showPreparationSummaryPreview && hasPreparationSummaryContent"
                class="flex flex-col gap-4 rounded-xl border border-border bg-white p-4"
              >
                <p class="text-sm font-semibold text-foreground">
                  {{ t('pages.import.importPage.finalize.summaryTitle') }}
                </p>

                <div v-if="preparationSummaryCards.length" class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div
                    v-for="card in preparationSummaryCards"
                    :key="card.key"
                    class="flex items-start gap-3 rounded-xl border p-4"
                    :class="card.containerClass"
                  >
                    <div
                      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                      :class="card.iconClass"
                    >
                      <component :is="card.icon" class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-2xl font-bold tabular-nums leading-none" :class="card.valueClass">
                        {{ card.value }}
                      </p>
                      <p class="mt-1.5 text-sm leading-snug" :class="card.labelClass">
                        {{ card.label }}
                      </p>
                    </div>
                  </div>
                </div>

                <Alert v-if="visibleErrors.length" variant="destructive">
                  <CircleX class="translate-y-0 self-center" />
                  <AlertTitle class="flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      class="flex min-w-0 items-center gap-2 text-left"
                      :aria-expanded="isIgnoredContactsDetailExpanded"
                      @click="isIgnoredContactsDetailExpanded = !isIgnoredContactsDetailExpanded"
                    >
                      <ChevronDown
                        class="h-4 w-4 shrink-0 transition-transform duration-200"
                        :class="{ 'rotate-180': isIgnoredContactsDetailExpanded }"
                      />
                      <span>{{ t('pages.import.historyPage.diagnosticsModal.ignoredContactsDetail') }}</span>
                      <span class="font-normal opacity-80">
                        ({{
                          t('pages.import.historyPage.diagnosticsModal.errorCount', {
                            count: diagnostics.totalCustomersWithError
                          })
                        }})
                      </span>
                    </button>
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      class="shrink-0 bg-background"
                      @click="emit('exportErrors', diagnostics.id, diagnostics.name)"
                    >
                      <Download class="h-4 w-4" />
                      {{ t('pages.import.historyPage.diagnosticsModal.exportErrors') }}
                    </Button>
                  </AlertTitle>

                  <AlertDescription v-show="isIgnoredContactsDetailExpanded" class="flex flex-col gap-2">
                    <div
                      v-for="error in visibleErrors"
                      :key="error.label"
                      class="rounded-lg border border-border/60 bg-background/90 p-3"
                    >
                      <p class="font-medium text-foreground">{{ error.label }}</p>
                      <p class="mt-1 text-xs text-muted-foreground">
                        {{ t('pages.import.historyPage.diagnosticsModal.errorCount', { count: error.count }) }} —
                        {{
                          t('pages.import.historyPage.diagnosticsModal.errorLines', {
                            lines: sampleLinesLabel(error.sampleLines)
                          })
                        }}
                      </p>
                    </div>
                    <p v-if="hiddenErrorsCount" class="text-xs opacity-80">
                      {{ t('pages.import.historyPage.diagnosticsModal.moreErrors', { count: hiddenErrorsCount }) }}
                    </p>
                  </AlertDescription>
                </Alert>
              </div>
            </div>

            <div v-if="step.key === 'processing'" class="mt-4 flex flex-col gap-4">
              <div
                v-if="showPreparationSummaryBeforeLaunch"
                class="flex flex-col gap-4 rounded-xl border border-border bg-white p-4"
              >
                <p class="text-sm font-semibold text-foreground">
                  {{ t('pages.import.importPage.finalize.summaryTitle') }}
                </p>

                <div v-if="preparationSummaryCards.length" class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div
                    v-for="card in preparationSummaryCards"
                    :key="card.key"
                    class="flex items-start gap-3 rounded-xl border p-4"
                    :class="card.containerClass"
                  >
                    <div
                      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
                      :class="card.iconClass"
                    >
                      <component :is="card.icon" class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-2xl font-bold tabular-nums leading-none" :class="card.valueClass">
                        {{ card.value }}
                      </p>
                      <p class="mt-1.5 text-sm leading-snug" :class="card.labelClass">
                        {{ card.label }}
                      </p>
                    </div>
                  </div>
                </div>

                <Alert v-if="visibleErrors.length" variant="destructive">
                  <CircleX class="translate-y-0 self-center" />
                  <AlertTitle class="flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      class="flex min-w-0 items-center gap-2 text-left"
                      :aria-expanded="isIgnoredContactsDetailExpanded"
                      @click="isIgnoredContactsDetailExpanded = !isIgnoredContactsDetailExpanded"
                    >
                      <ChevronDown
                        class="h-4 w-4 shrink-0 transition-transform duration-200"
                        :class="{ 'rotate-180': isIgnoredContactsDetailExpanded }"
                      />
                      <span>{{ t('pages.import.historyPage.diagnosticsModal.ignoredContactsDetail') }}</span>
                      <span class="font-normal opacity-80">
                        ({{
                          t('pages.import.historyPage.diagnosticsModal.errorCount', {
                            count: diagnostics.totalCustomersWithError
                          })
                        }})
                      </span>
                    </button>
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      class="shrink-0 bg-background"
                      @click="emit('exportErrors', diagnostics.id, diagnostics.name)"
                    >
                      <Download class="h-4 w-4" />
                      {{ t('pages.import.historyPage.diagnosticsModal.exportErrors') }}
                    </Button>
                  </AlertTitle>

                  <AlertDescription v-show="isIgnoredContactsDetailExpanded" class="flex flex-col gap-2">
                    <div
                      v-for="error in visibleErrors"
                      :key="error.label"
                      class="rounded-lg border border-border/60 bg-background/90 p-3"
                    >
                      <p class="font-medium text-foreground">{{ error.label }}</p>
                      <p class="mt-1 text-xs text-muted-foreground">
                        {{ t('pages.import.historyPage.diagnosticsModal.errorCount', { count: error.count }) }} —
                        {{
                          t('pages.import.historyPage.diagnosticsModal.errorLines', {
                            lines: sampleLinesLabel(error.sampleLines)
                          })
                        }}
                      </p>
                    </div>
                    <p v-if="hiddenErrorsCount" class="text-xs opacity-80">
                      {{ t('pages.import.historyPage.diagnosticsModal.moreErrors', { count: hiddenErrorsCount }) }}
                    </p>
                  </AlertDescription>
                </Alert>
              </div>

              <div v-if="isProcessingStarted" class="flex flex-col gap-2">
                <div class="mb-2 flex items-center justify-between gap-2 text-sm">
                  <span class="font-medium text-foreground">
                    {{ t('pages.import.historyPage.diagnosticsModal.processedContacts') }}
                  </span>
                  <span class="font-semibold text-primary">{{ processingPercentage }}%</span>
                </div>
                <Progress :model-value="processingPercentage" class="h-2.5" />
                <p class="mt-1.5 text-xs text-muted-foreground">
                  {{ diagnostics.totalCustomersProcessed }} / {{ diagnostics.totalCustomers }}
                  {{ t('pages.import.historyPage.diagnosticsModal.processedContacts').toLowerCase() }}
                </p>
              </div>

              <div v-if="showLaunchActions && isWaitingForLaunchConfirmation">
                <Button
                  variant="default-primary"
                  type="button"
                  class="w-fit"
                  :disabled="isLaunching"
                  @click="emit('launch')"
                >
                  <Loader2 v-if="isLaunching" class="mr-2 h-4 w-4 animate-spin" />
                  <Play v-else class="mr-2 h-4 w-4" />
                  {{ t('pages.import.importPage.buttons.launchAfterPreparation') }}
                </Button>
              </div>
            </div>
          </div>
        </li>
      </ol>
    </div>
  </div>
</template>
